import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import './DataTree.module.css';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { useAppSelector } from '../../../Store/Store';
import html2canvas from 'html2canvas';

// const [showDataTree, setShowDataTree] = React.useState<boolean>(false);


// Custom Node Component - DoubleLoop Style
const CollapsibleNode = ({ data, id }) => {
  const isExpanded = !data.isCollapsed;
  const isSelected = Boolean(data.isSelected);
  const hasPrimaryMetric = Boolean(data.primaryMetricValue);
  const hasSecondaryLine = Boolean(data.revenueChange || data.revenueGrowthPct);
  const hasStatus = Boolean(data.status);
  const shouldShowMeta = hasPrimaryMetric || hasSecondaryLine || hasStatus;

  const getSignedValueColor = (rawValue, defaultColor) => {
    if (rawValue === undefined || rawValue === null) return defaultColor;

    const text = String(rawValue).trim().toLowerCase();
    if (!text || text.includes('nan')) return defaultColor;

    const numberMatches = text.match(/[+-]?\d*\.?\d+/g);
    if (!numberMatches) return defaultColor;

    const numbers = numberMatches
      .map((num) => Number(num))
      .filter((num) => Number.isFinite(num));

    if (numbers.some((num) => num < 0)) return '#f44336';
    if (numbers.some((num) => num > 0)) return '#4caf50';
    return defaultColor;
  };

  const primaryMetricColor = '#1976d2';
  const secondaryMetricColor = getSignedValueColor(data.revenueGrowthPct || data.revenueChange, '#424242');

  return (
    <div
      style={{
        background: isSelected ? '#fff3e0' : isExpanded ? '#e3f2fd' : 'white',
        border: isSelected ? '2px solid #ef6c00' : isExpanded ? '1px solid #1976d2' : '1px solid #e0e0e0',
        borderRadius: '5px',
        padding: '5px',
        minWidth: '72px',
        maxWidth: '90px',
        position: 'relative',
        pointerEvents: 'auto',
        boxShadow: isSelected
          ? '0 0 0 2px rgba(239,108,0,0.22), 0 2px 6px rgba(0,0,0,0.16)'
          : '0 1px 5px rgba(0,0,0,0.08)',
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#555', width: '4px', height: '4px' }}
      />
      <div style={{ paddingRight: data.hasChildren ? '16px' : '0' }}>
        <div
          style={{
            fontWeight: '600',
            fontSize: '6.6px',
            marginBottom: shouldShowMeta ? '2.4px' : '0',
            wordBreak: 'break-word',
            color: '#1a1a1a',
            lineHeight: '1.2',
          }}
          title={data.insight || data.label}
        >
          {data.label}
        </div>

        {hasPrimaryMetric && (
          <div
            style={{
              fontSize: '8.2px',
              fontWeight: '700',
              color: primaryMetricColor,
              marginBottom: hasSecondaryLine || hasStatus ? '1.4px' : '0',
            }}
            title={`Revenue: ${data.primaryMetricValue}`}
          >
            {data.primaryMetricValue}
          </div>
        )}

        {hasSecondaryLine && (
          <div
            style={{
              fontSize: '6px',
              color: secondaryMetricColor,
              marginBottom: hasStatus ? '1.2px' : '0',
              lineHeight: '1.1',
            }}
            title={`${data.revenueChange || ''} ${data.revenueGrowthPct || ''}`}
          >
            {data.revenueChange || ''}{data.revenueChange && data.revenueGrowthPct ? ' • ' : ''}{data.revenueGrowthPct || ''}
          </div>
        )}

        {hasStatus && (
          <div
            style={{
              fontSize: '5.76px',
              color: data.status.includes('↓') ? '#f44336' : '#4caf50',
              display: 'flex',
              alignItems: 'center',
              gap: '1.2px',
              fontWeight: '500',
            }}
          >
            <span>{data.status}</span>
          </div>
        )}
      </div>

      {data.hasChildren && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            data.onToggleCollapse(id);
          }}
          style={{
            position: 'absolute',
            right: '2.4px',
            top: '50%',
            transform: 'translateY(-50%)',
            padding: '1.8px 3px',
            fontSize: '4.8px',
            cursor: 'pointer',
            background: isExpanded ? '#1976d2' : '#f5f5f5',
            color: isExpanded ? 'white' : '#666',
            border: isExpanded ? 'none' : '1px solid #e0e0e0',
            borderRadius: '2.4px',
            pointerEvents: 'auto',
            zIndex: 1000,
            minWidth: '12px',
            textAlign: 'center'
          }}
        >
          {data.isCollapsed ? '▶' : '▼'}
        </button>
      )}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#555', width: '4px', height: '4px' }}
      />
    </div>
  );
};

const nodeTypes = {
  collapsible: CollapsibleNode,
};

const getValueColor = (value, defaultColor = '#1565c0') => {
  if (!value || value === '—') return defaultColor;
  const text = String(value).trim();
  // Only color based on explicit +/- sign in the text
  if (/(?:^|[\s(])-\d/.test(text)) return '#f44336';
  if (/(?:^|[\s(])\+\d/.test(text)) return '#4caf50';
  return '#1565c0';
};

// For Revenue Growth: color based on actual numeric value (positive/negative), not explicit sign
const getNumericColor = (value, defaultColor = '#334e68') => {
  if (!value || value === '—') return defaultColor;
  const nums = String(value).match(/[+-]?\d*\.?\d+/g);
  if (!nums) return defaultColor;
  const parsed = nums.map(Number).filter(Number.isFinite);
  if (parsed.some((n) => n < 0)) return '#f44336';
  if (parsed.some((n) => n > 0)) return '#4caf50';
  return defaultColor;
};

const renderInsightText = (text) => {
  if (!text) return 'No insight available for this node.';

  const numRegex = /(\(?[+-]?\d[\d.,]*\s*[%KkMmBb]?(?:\s*(?:pts|K|M|B))?\s*\)?)/g;

  const colorizeSegment = (str, keyPrefix) => {
    const parts = str.split(numRegex);
    return parts.map((seg, j) => {
      if (!seg) return null;
      if (/\d/.test(seg)) {
        return <span key={`${keyPrefix}-${j}`} style={{ color: getValueColor(seg, '#1565c0') }}>{seg}</span>;
      }
      return <span key={`${keyPrefix}-${j}`}>{seg}</span>;
    });
  };

  // Split into sentences at ". " or "." at end, treat each as a separate point
  const sentences = String(text).split(/\.(?:\s+|$)/).filter((s) => s.trim());
  return (
    <ul style={{ margin: 0, paddingLeft: '18px', listStyleType: 'disc' }}>
      {sentences.map((sentence, si) => {
        // Split by ** to toggle bold/non-bold
        const parts = sentence.split('**');
        return (
          <li key={si} style={{ marginBottom: si < sentences.length - 1 ? '6px' : 0 }}>
            {parts.map((part, i) => {
              if (!part) return null;
              const isBold = i % 2 === 1;
              if (isBold) {
                return <span key={i} style={{ fontWeight: 700 }}>{colorizeSegment(part, `${si}-${i}`)}</span>;
              }
              return <span key={i}>{colorizeSegment(part, `${si}-${i}`)}</span>;
            })}
            .
          </li>
        );
      })}
    </ul>
  );
};

// let replacequotes = (text) => {
  // let result = rawString.replaceAll('red"', "red'");
  // result = rawString.replaceAll('="', "='");
// }

function cleanJsonString(rawJson) {
  let cleaned = rawJson;

  // line breaks, tabs
  cleaned = cleaned.replace(/[\r\n\t]+/g, " ");

  // extra spaces
  cleaned = cleaned.replace(/\s+/g, " ");

  // spaces around : and ,
  cleaned = cleaned.replace(/\s*:\s*/g, ":");
  cleaned = cleaned.replace(/\s*,\s*/g, ",");

  // Remove trailing commas before } or ]
  cleaned = cleaned.replace(/,\s*([}\]])/g, "$1");

  // Trim overall string
  cleaned = cleaned.trim();

  return cleaned;
}
const TreeVisualizationInner = () => {
  useEffect(() => {
    function hideError(e) {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    }
    window.addEventListener('error', hideError);
    return () => {
      window.removeEventListener('error', hideError);
    };
  }, []);

  const rawString = useAppSelector((state) => state.chatBotDetails.treeInfo);

  const treeData = useMemo(() => {
    try {
      if (!rawString) return null;
      if (typeof rawString === 'object') return rawString;
      let parsed = JSON.parse(rawString);
      // Handle double-encoded JSON string
      if (typeof parsed === 'string') {
        parsed = JSON.parse(cleanJsonString(JSON.parse(rawString)));
      }
      return parsed;
    } catch (e) {
      console.error('Failed to parse treeInfo:', e);
      return null;
    }
  }, [rawString]);

  const { zoomIn, zoomOut, fitView, getViewport, zoomTo } = useReactFlow();
  const [zoomLevel, setZoomLevel] = useState(200);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const flowRef = useRef(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Build initial graph structure
  const { allNodes, allEdges, childrenMap, parentMap } = useMemo(() => {
    if (!treeData || typeof treeData !== 'object' || Object.keys(treeData).length === 0) {
      return { allNodes: [], allEdges: [], childrenMap: {}, parentMap: {} };
    }
    const nodes = [];
    const edges = [];
    const childrenMap = {};
    const parentMap = {};
    let nodeId = 0;

    const getNodeData = (value = {}) => {
      return {
        // Top section
        primaryMetricValue: value?.revenue_k || value?.value || '',
        revenueChange: value?.revenue_k_chg || '',
        revenueGrowthPct: value?.revenue_k_growth_pct || '',
        volumeGrowthPct: value?.volume_k_growth_pct || '',
        transactionsGrowthPct: value?.transactions_k_growth_pct || '',
        revenueSharePct: value?.revenue_share_pct || '',
        revenueShareChgPts: value?.revenue_share_pct_chg_pts || '',
        // Drivers
        promoRevenueGrowthPct: value?.promo_revenue_k_growth_pct || '',
        promoRevenueContribPct: value?.promo_revenue_contrib_pct || '',
        nonPromoRevenueGrowthPct: value?.non_promo_revenue_k_growth_pct || '',
        wdPct: value?.wd_pct || '',
        wdChangePts: value?.wd_pct_chg_pts || '',
        ndPct: value?.nd_pct || '',
        ndChangePts: value?.nd_pct_chg_pts || '',
        oosPct: value?.oos_pct || '',
        oosChangePts: value?.oos_pct_chg_pts || '',
        oss: value?.oss || '',
        ossTarget: value?.oss_target || '',
        ossGrowthPct: value?.oss_growth_pct || '',
        sosPct: value?.sos_pct || '',
        sosTarget: value?.sos_target || '',
        sosChangePts: value?.sos_pct_chg_pts || '',
        // Other
        totalRevenueContribPct: value?.total_revenue_contrib_pct || '',
        revenueChgContribPct: value?.revenue_chg_contrib_pct || '',
        ros: value?.ros || '',
        rosGrowthPct: value?.ros_growth_pct || '',
        planoComplianceChangePts: value?.plano_compliance_pct_chg_pts || '',
        status: value?.status || '',
        metric: value?.metric || '',
        value: value?.value || '',
        insight: value?.insight || '',
      };
    };

    const createNode = (label, isRoot = false, nodeData = {}) => {
      const id = `node-${nodeId++}`;
      nodes.push({ id, label, isRoot, ...nodeData });
      childrenMap[id] = [];
      return id;
    };

    const processNode = (data, parentId = null, isRoot = false) => {
      if (typeof data === 'string') {
        const id = createNode(data, false);
        if (parentId) {
          edges.push({ source: parentId, target: id });
          childrenMap[parentId].push(id);
        }
        return id;
      } else if (typeof data === 'object' && data !== null) {
        if (Array.isArray(data)) {
          data.forEach((item) => processNode(item, parentId, false));
        } else {
          for (const [key, value] of Object.entries(data)) {
            const nodeData = getNodeData(value);
            const id = createNode(key, isRoot, nodeData);
            if (parentId) {
              edges.push({ source: parentId, target: id });
              childrenMap[parentId].push(id);
              parentMap[id] = parentId;
            }

            if (value && typeof value === 'object' && value.children) {
              if (Array.isArray(value.children)) {
                value.children.forEach((child) => {
                  processNode(child, id, false);
                });
              }
            }
          }
        }
      }
    };

    processNode(treeData, null, true);
    return { allNodes: nodes, allEdges: edges, childrenMap, parentMap };
  }, [treeData]);

  const [selectedNodeId, setSelectedNodeId] = useState(() => allNodes[0]?.id || null);

  const nodeById = useMemo(() => {
    const map = {};
    allNodes.forEach((node) => {
      map[node.id] = node;
    });
    return map;
  }, [allNodes]);

  const selectedNode = selectedNodeId ? nodeById[selectedNodeId] : null;
  const [isInsightPanelOpen, setIsInsightPanelOpen] = useState(true);
console.log('[DataTree] Selected node:', selectedNode?.insight, 'full node data:', selectedNode);
  const selectedNodePath = useMemo(() => {
    if (!selectedNodeId) return '';
    const labels = [];
    let current = selectedNodeId;

    while (current) {
      const node = nodeById[current];
      if (!node) break;
      labels.unshift(node.label);
      current = parentMap[current];
    }

    return labels.join(' > ');
  }, [selectedNodeId, nodeById, parentMap]);

  // Initialize collapsed nodes - collapse everything except first level
  const initialCollapsedState = useMemo(() => {
    const initialCollapsed = new Set();

    // Find nodes at level 0 (keep only root expanded)
    const firstLevel = new Set();

    // Add root node (level 0)
    if (allNodes.length > 0) {
      const rootId = allNodes[0].id;
      firstLevel.add(rootId);
    }

    // Collapse all other nodes
    allNodes.forEach((node) => {
      if (!firstLevel.has(node.id) && childrenMap[node.id].length > 0) {
        initialCollapsed.add(node.id);
      }
    });

    return initialCollapsed;
  }, [allNodes, childrenMap]);

  const [collapsedNodes, setCollapsedNodes] = useState(() => initialCollapsedState);

  // Reset state when treeData changes
  useEffect(() => {
    setSelectedNodeId(allNodes[0]?.id || null);
    setCollapsedNodes(new Set(initialCollapsedState));
  }, [allNodes, initialCollapsedState]);

  const handleReset = useCallback(() => {
    setCollapsedNodes(new Set(initialCollapsedState));
    setSelectedNodeId(allNodes[0]?.id || null);
    // Wait for React to re-render with new collapsed state and dagre to recompute layout
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fitView({ padding: 0.3, duration: 200 });
        setTimeout(() => {
          zoomTo(2, { duration: 300 });
          setZoomLevel(200);
          handleFitView();
        }, 250);
      });
    });
  }, [initialCollapsedState, allNodes, zoomTo, fitView]);

  const handleExpandAll = useCallback(() => {
    setCollapsedNodes(new Set());
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          setZoomLevel(Math.round(getViewport().zoom * 100));
          fitView({ padding: 0.2, duration: 300 });
        }, 250);
      });
    });
  }, [fitView, getViewport]);

  const handleZoomIn = useCallback(() => {
    const currentZoom = getViewport().zoom;
    const newZoom = Math.min(currentZoom + 0.1, 4);
    zoomTo(newZoom, { duration: 200 });
    setZoomLevel(Math.round(newZoom * 100));
  }, [getViewport, zoomTo]);

  const handleZoomOut = useCallback(() => {
    const currentZoom = getViewport().zoom;
    const newZoom = Math.max(currentZoom - 0.1, 0.1);
    zoomTo(newZoom, { duration: 200 });
    setZoomLevel(Math.round(newZoom * 100));
  }, [getViewport, zoomTo]);

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.2, duration: 300 });
  }, [fitView]);

  const handleInit = useCallback((reactFlowInstance) => {
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.3 });
      reactFlowInstance.zoomTo(2, { duration: 300 });
      setZoomLevel(200);
    }, 100);
  }, []);

  const handleMove = useCallback((event, viewport) => {
    setZoomLevel(Math.round(viewport.zoom * 100));
  }, []);

  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const handleDownloadImage = useCallback(() => {
    const el = flowRef.current;
    if (!el) return;
    html2canvas(el, { useCORS: true, backgroundColor: '#fafafa' }).then((canvas) => {
      const link = document.createElement('a');
      link.download = 'performance-tree.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  }, []);

  const handleToggleCollapse = useCallback((nodeId) => {
    console.log('Toggle called for:', nodeId);
    setCollapsedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        // Expanding: remove from collapsed set and add all children to collapsed set
        newSet.delete(nodeId);
        // Collapse all direct children so only next level shows
        childrenMap[nodeId]?.forEach((childId) => {
          newSet.add(childId);
        });
      } else {
        // Collapsing: add to collapsed set
        newSet.add(nodeId);
      }
      console.log('New collapsed nodes:', newSet);
      setTimeout(() => {
          setZoomLevel(Math.round(getViewport().zoom * 100));
          fitView({ padding: 0.2, duration: 300 });
      }, 250);
      return newSet;
    });
  }, [childrenMap]);

  const handleNodeClick = useCallback((_, node) => {
    setSelectedNodeId(node.id);
    setIsInsightPanelOpen(true);
  }, []);

  // Update nodes and edges when collapse state changes
  useEffect(() => {
    // Determine visible nodes
    const visibleNodeIds = new Set();
    const queue = [allNodes[0]?.id];

    while (queue.length > 0) {
      const nodeId = queue.shift();
      if (!nodeId || visibleNodeIds.has(nodeId)) continue;

      visibleNodeIds.add(nodeId);

      if (!collapsedNodes.has(nodeId)) {
        childrenMap[nodeId]?.forEach((child) => {
          queue.push(child);
        });
      }
    }

    // Create nodes for visible items
    const nodeMap = {};
    const flowNodes = [];

    allNodes.forEach((node) => {
      if (visibleNodeIds.has(node.id)) {
        flowNodes.push({
          id: node.id,
          data: {
            label: node.label,
            metric: node.metric,
            value: node.value,
            primaryMetricValue: node.primaryMetricValue,
            revenueChange: node.revenueChange,
            revenueGrowthPct: node.revenueGrowthPct,
            volumeGrowthPct: node.volumeGrowthPct,
            transactionsGrowthPct: node.transactionsGrowthPct,
            revenueSharePct: node.revenueSharePct,
            revenueShareChgPts: node.revenueShareChgPts,
            promoRevenueGrowthPct: node.promoRevenueGrowthPct,
            promoRevenueContribPct: node.promoRevenueContribPct,
            nonPromoRevenueGrowthPct: node.nonPromoRevenueGrowthPct,
            wdPct: node.wdPct,
            wdChangePts: node.wdChangePts,
            ndPct: node.ndPct,
            ndChangePts: node.ndChangePts,
            oosPct: node.oosPct,
            oosChangePts: node.oosChangePts,
            oss: node.oss,
            ossTarget: node.ossTarget,
            ossGrowthPct: node.ossGrowthPct,
            sosPct: node.sosPct,
            sosTarget: node.sosTarget,
            sosChangePts: node.sosChangePts,
            totalRevenueContribPct: node.totalRevenueContribPct,
            revenueChgContribPct: node.revenueChgContribPct,
            ros: node.ros,
            rosGrowthPct: node.rosGrowthPct,
            planoComplianceChangePts: node.planoComplianceChangePts,
            status: node.status,
            insight: node.insight,
            isSelected: selectedNodeId === node.id,
            isRoot: node.isRoot,
            hasChildren: childrenMap[node.id].length > 0,
            isCollapsed: collapsedNodes.has(node.id),
            onToggleCollapse: handleToggleCollapse,
          },
          position: { x: 0, y: 0 },
          type: 'collapsible',
        });
        nodeMap[node.id] = true;
      }
    });

    // Create edges for visible connections - DoubleLoop style
    const flowEdges = allEdges
      .filter((edge) => nodeMap[edge.source] && nodeMap[edge.target])
      .map((edge, idx) => ({
        id: `edge-${idx}`,
        source: edge.source,
        target: edge.target,
        type: 'smoothstep',
        animated: true,
        style: {
          stroke: '#b0b0b0',
          strokeWidth: 1,
          strokeDasharray: '3,3'
        },
        label: nodeById[edge.target]?.totalRevenueContribPct || '—',
        labelStyle: {
          fill: 'white',
          fontWeight: 600,
          fontSize: 5.0
        },
        labelBgStyle: {
          fill: '#1565c0',
          fillOpacity: 1
        },
        labelBgPadding: [2, 5],
        labelBgBorderRadius: 4,
      }));

    // Apply Dagre layout - HORIZONTAL (Left to Right)
    if (flowNodes.length > 0) {
      const dagreGraph = new dagre.graphlib.Graph();
      dagreGraph.setDefaultEdgeLabel(() => ({}));
      dagreGraph.setGraph({ rankdir: 'LR', nodesep: 30, ranksep: 60 });

      flowNodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: 72, height: 36 });
      });

      flowEdges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
      });

      dagre.layout(dagreGraph);

      flowNodes.forEach((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        node.position = {
          x: nodeWithPosition.x - 36,
          y: nodeWithPosition.y - 18,
        };
      });
    }

    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [collapsedNodes, selectedNodeId, allNodes, allEdges, childrenMap, handleToggleCollapse, nodeById, setNodes, setEdges]);

  if (!treeData || allNodes.length === 0) {
    return (
      <div style={{ width: '100%', height: '100%', background: '#fafafa', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px 8px 20px', background: '#ffffff', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ fontWeight: 700, fontSize: '18px', color: '#1a1a1a', lineHeight: 1.3 }}>Performance Tree</div>
          <div style={{ fontSize: '13px', color: '#627d98', marginTop: '2px', paddingBottom: '8px' }}>Interactive decomposition analysis</div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '14px', color: '#627d98' }}>Data not available</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', background: '#fafafa', display: 'flex', flexDirection: 'row' }}>
      {/* Left side: Header + Toolbar + Flow */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header */}
        <div style={{ padding: '16px 20px 8px 20px', background: '#ffffff' }}>
          <div style={{ fontWeight: 700, fontSize: '18px', color: '#1a1a1a', lineHeight: 1.3 }}>Performance Tree</div>
          <div style={{ fontSize: '13px', color: '#627d98', marginTop: '2px' }}>Interactive decomposition analysis</div>
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', background: '#ffffff', borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap', gap: '8px' }}>
          {/* Left controls */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={handleReset}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 16px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6"/><path d="M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/></svg>
              Reset View
            </button>
            <button
              onClick={handleExpandAll}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 16px', background: '#1565c0', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Expand All
            </button>
          </div>

          {/* Right controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={handleZoomOut}
              title="Zoom Out"
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '50%', cursor: 'pointer', fontSize: '18px', color: '#424242', lineHeight: 1 }}
            >
              −
            </button>
            <span style={{ padding: '0 8px', fontSize: '13px', fontWeight: 600, color: '#424242', minWidth: '42px', textAlign: 'center', userSelect: 'none' }}>{zoomLevel}%</span>
            <button
              onClick={handleZoomIn}
              title="Zoom In"
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '50%', cursor: 'pointer', fontSize: '18px', color: '#424242', lineHeight: 1 }}
            >
              +
            </button>
            <button
              onClick={handleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '50%', cursor: 'pointer', color: '#424242', marginLeft: '4px' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {isFullscreen ? (
                  <><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></>
                ) : (
                  <><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Flow area */}
        <div ref={flowRef} style={{ flex: 1, position: 'relative', minHeight: 0 }}>
          {/* {!isInsightPanelOpen && (
            <button
              onClick={() => setIsInsightPanelOpen(true)}
              style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 1000, display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 16px', background: '#1976d2', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}
            >
              Open Insights
            </button>
          )} */}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            nodeTypes={nodeTypes}
            onInit={handleInit}
            minZoom={0.1}
            maxZoom={4}
            onMove={handleMove}
          >
            <Background color="#e0e0e0" gap={16} size={0.5} />
          </ReactFlow>
        </div>
      </div>

      {/* Right side: Node Details panel - full height */}
      {isInsightPanelOpen && (
        <aside
          style={{
            width: '360px',
            borderLeft: '1px solid #e0e0e0',
            background: '#f8faff',
            padding: '10px',
            overflowY: 'auto',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
              paddingBottom: '6px',
              borderBottom: '1px solid #e6ecf5',
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#102a43' }}>
              Node details
            </div>
            <button
              onClick={() => setIsInsightPanelOpen(false)}
              style={{
                padding: '4px 8px',
                background: '#ffffff',
                color: '#334e68',
                border: '1px solid #d9e2ec',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: '600',
              }}
            >
              Close
            </button>
          </div>

          {!selectedNode && (
            <div
              style={{
                fontSize: '13px',
                color: '#486581',
                lineHeight: 1.6,
                background: '#ffffff',
                border: '1px solid #e6ecf5',
                borderRadius: '10px',
                padding: '12px',
              }}
            >
              Select a node to view insights and KPIs.
            </div>
          )}

          {selectedNode && (
            <>
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #e6ecf5',
                  borderRadius: '10px',
                  padding: '8px 10px',
                  marginBottom: '8px',
                }}
              >
                <div style={{ fontSize: '10px', color: '#627d98', marginBottom: '3px', fontWeight: 600 }}>
                  Selected Node
                </div>
                <div style={{ fontSize: '13px', color: '#102a43', fontWeight: 700, marginBottom: '5px' }}>
                  {selectedNode.label}
                </div>
                <div
                  style={{
                    fontSize: '10px',
                    color: '#486581',
                    whiteSpace: 'nowrap',
                    overflowX: 'auto',
                    lineHeight: 1.3,
                  }}
                >
                  {selectedNodePath}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: '6px',
                  marginBottom: '8px',
                }}
              >
                {/* Box 1: Revenue */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e6ecf5',
                    borderRadius: '10px',
                    padding: '7px 8px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '10px', color: '#627d98', marginBottom: '3px', fontWeight: 600 }}>Revenue</div>
                  <div style={{ fontSize: '12px', color: '#1565c0', fontWeight: 700, lineHeight: 1.2 }}>
                    {selectedNode.primaryMetricValue || '—'}
                  </div>
                  {(selectedNode.revenueChange || selectedNode.revenueGrowthPct) && (
                    <div style={{ fontSize: '9px', color: getNumericColor(selectedNode.revenueGrowthPct || selectedNode.revenueChange, '#829ab1'), marginTop: '2px', lineHeight: 1.2 }}>
                      {selectedNode.revenueChange && <span style={{fontSize: '10px',}}>{selectedNode.revenueChange}</span>}
                      {selectedNode.revenueChange && selectedNode.revenueGrowthPct && <span>, </span>}
                      {selectedNode.revenueGrowthPct && <span style={{fontSize: '10px',}}>{selectedNode.revenueGrowthPct}</span>}
                    </div>
                  )}
                </div>

                {/* Box 2: Volume Growth & Trans. Growth (no box label) */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e6ecf5',
                    borderRadius: '10px',
                    padding: '7px 8px',
                    textAlign: 'center',
                  }}
                >
                  {selectedNode.volumeGrowthPct && (
                    <div style={{ marginBottom: '4px' }}>
                      <div style={{ fontSize: '9px', color: '#829ab1' }}>Volume Growth</div>
                      <div style={{ fontSize: '11px', color: getNumericColor(selectedNode.volumeGrowthPct, '#1565c0'), fontWeight: 600 }}>{selectedNode.volumeGrowthPct}</div>
                    </div>
                  )}
                  {selectedNode.transactionsGrowthPct && (
                    <div>
                      <div style={{ fontSize: '9px', color: '#829ab1' }}>Trans. Growth</div>
                      <div style={{ fontSize: '11px', color: getNumericColor(selectedNode.transactionsGrowthPct, '#1565c0'), fontWeight: 600 }}>{selectedNode.transactionsGrowthPct}</div>
                    </div>
                  )}
                  {!selectedNode.volumeGrowthPct && !selectedNode.transactionsGrowthPct && (
                    <div style={{ fontSize: '11px', color: '#829ab1' }}>—</div>
                  )}
                </div>

                {/* Box 3: Revenue Share */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e6ecf5',
                    borderRadius: '10px',
                    padding: '7px 8px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '10px', color: '#627d98', marginBottom: '3px', fontWeight: 600 }}>Revenue Share</div>
                  <div style={{ fontSize: '12px', color: '#1565c0', fontWeight: 700, lineHeight: 1.2 }}>
                    {selectedNode.revenueSharePct || '—'}
                  </div>
                  {selectedNode.revenueShareChgPts && (
                    <div style={{ fontSize: '9px', color: getNumericColor(selectedNode.revenueShareChgPts, '#1565c0'), marginTop: '2px', lineHeight: 1.2 }}>
                      {selectedNode.revenueShareChgPts}
                    </div>
                  )}
                </div>
              </div>

              {/* Drivers Section */}
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #e6ecf5',
                  borderRadius: '10px',
                  padding: '8px',
                  marginBottom: '8px',
                }}
              >
                <div style={{ fontSize: '10px', color: '#627d98', marginBottom: '6px', fontWeight: 600 }}>
                  Drivers
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: '6px',
                  }}
                >
                  {[
                    { label: 'Promo Growth', value: selectedNode.promoRevenueGrowthPct, isBlue: false },
                    { label: 'Promo Contrib.', value: selectedNode.promoRevenueContribPct, isBlue: true },
                    { label: 'Non-Promo Growth', value: selectedNode.nonPromoRevenueGrowthPct, isBlue: false },
                    { label: 'WD', value: selectedNode.wdPct, isBlue: true, bottom: selectedNode.wdChangePts, bottomIsBlue: false },
                    { label: 'ND', value: selectedNode.ndPct, isBlue: true, bottom: selectedNode.ndChangePts, bottomIsBlue: false },
                    { label: 'ROS', value: selectedNode.ros, isBlue: true, bottom: selectedNode.rosGrowthPct, bottomIsBlue: false },
                    { label: selectedNode.ossTarget ? 'OSS (Target)' : 'OSS', value: selectedNode.ossTarget ? `${selectedNode.oss} (${selectedNode.ossTarget})` : selectedNode.oss, isBlue: true, bottom: selectedNode.ossGrowthPct || '', bottomIsBlue: false },
                    { label: selectedNode.sosTarget ? 'SOS (Target)' : 'SOS', value: selectedNode.sosTarget ? `${selectedNode.sosPct} (${selectedNode.sosTarget})` : selectedNode.sosPct, isBlue: true, bottom: selectedNode.sosChangePts || '', bottomIsBlue: false },
                    { label: 'OOS', value: selectedNode.oosPct, isBlue: true, bottom: selectedNode.oosChangePts, bottomIsBlue: false, invertColor: true },
                  ].filter((item) => item.value).map((item, idx) => {
                    const getColor = (val, def) => {
                      if (!item.invertColor) return getNumericColor(val, def);
                      const c = getNumericColor(val, def);
                      if (c === '#f44336') return '#4caf50';
                      if (c === '#4caf50') return '#f44336';
                      return c;
                    };
                    return (
                    <div key={idx} style={{ background: '#f8fbff', border: '1px solid #e6ecf5', borderRadius: '7px', padding: '5px 6px', overflow: 'hidden', textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: '#829ab1' }}>{item.label}</div>
                      <div style={{ fontSize: '11px', color: item.isBlue ? '#1565c0' : getColor(item.value, '#1565c0'), fontWeight: 600, }}>{item.value}</div>
                      {item.bottom && (
                        <div style={{ fontSize: '9px', color: item.bottomIsBlue ? '#1565c0' : getColor(item.bottom, '#1565c0'), marginTop: '1px' }}>{item.bottom}</div>
                      )}
                    </div>
                    );
                  }
                  )}
                </div>
              </div>

              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #e6ecf5',
                  borderLeft: '1px solid #e6ecf5',
                  borderRadius: '10px',
                  padding: '8px 10px',
                }}
              >
                <div style={{ fontSize: '10px', color: '#627d98', marginBottom: '4px', fontWeight: 600 }}>Insight Summary</div>
                <div style={{ fontSize: '12px', color: '#243b53', lineHeight: 1.35 }}>
                  {renderInsightText(selectedNode.insight)}
                </div>
              </div>
            </>
          )}
        </aside>
      )}
    </div>
  );
};

const TreeVisualization = () => (
  <ReactFlowProvider>
    <TreeVisualizationInner />
  </ReactFlowProvider>
);

export default TreeVisualization;
